<template>
  <div
    :id="item.supplierOrganizationId"
    class="pa-8 supplier-card main__light_green rounded-lg"
    :class="{'mb-4': !last, 'secondary__green_chosen': opened}"
    @click="openAll">
    <div class="flex">
      <div class="width-80">
        <div
          class="text-start pb-4">
          <router-link
            class="hover-underline h3-headline"
            :to=" {name: 'supplier', params: { id: item.supplierOrganizationId, org: $route.params.org}}">
            {{ item.egrpou }} {{ item.organizationTitle && item.egrpou ? ' - ' : '' }} {{ item.organizationTitle }}
          </router-link>
        </div>
        <div class="flex">
          <div
            class="width-30">
            <div class="mb-2 h9-label-bold black--text">
              {{ $t('Tenders.Offers') }}
            </div>
            <template
              v-if="item.bids">
              <KeyValue
                :caption="$t('Suppliers.Total')"
                class="mb-2"
                :value="bidsInAmountOf(item.bids.count, item.bids.pricesSum)"/>
              <KeyValue
                :caption="$t('Suppliers.InMyBidding')"
                class="mb-2"
                :value="bidsInAmountOf(item.bids.countInOwnTender, item.bids.pricesSumInOwnTender)"/>
              <KeyValue
                :caption="$t('Suppliers.DateLastSubmittedProposal')"
                :value="formatBy( item.lastBidDate, 'DD.MM.YYYY')"/>
            </template>
          </div>
          <div
            v-if="item.wins && item.wins.count"
            class="width-30">
            <div class="mb-2 h9-label-bold black--text">
              {{ $t('Tenders.Offers') }}
            </div>
            <KeyValue
              :caption="$t('Suppliers.Total')"
              class="mb-2"
              :value="bidsInAmountOf(item.wins.count, item.wins.pricesSum)"/>
            <KeyValue
              :caption="$t('Suppliers.InMyBidding')"
              class="mb-2"
              :value="bidsInAmountOf(item.wins.countInOwnTender, item.wins.pricesSumInOwnTender)"/>
            <KeyValue
              :caption="$t('Suppliers.DateOfLastVictory')"
              :value="formatBy( item.lastWinDate, 'DD.MM.YYYY')"/>
          </div>
          <div
            v-if="item.revisionStatus == 'VERIFIED'"
            class="width-30">
            <div class="mb-2 h9-label-bold black--text">
              {{ $t('Suppliers.Audit') }}
            </div>
            <KeyValue
              v-if="status"
              :caption="$t('Purchasing.Status')"
              class="mb-2"
              :value="decision_status.title"/>
            <KeyValue
              :caption="$t('Suppliers.DateInspection')"
              class="mb-2"
              :value="formatBy( item.revisionDate, 'DD.MM.YYYY')"/>
            <KeyValue
              :caption="$t('Suppliers.InspectionRelevance')"
              :value="formatBy( item.statusActualTo, 'DD.MM.YYYY')"/>
          </div>
        </div>
      </div>
      <div class="width-20">
        <div>
          <StatusChip
            key-name="verificationStatusId"
            :status="status"
            value-name="title"/>
          <SupplierOnSite
            class="py-4"
            :supplier="item"/>
          <KeyValue
            :caption="$t('Tenders.Rating')">
            <div class="flex flex-between">
              {{ item.rating || '–' }}
              <sTooltip
                :border="false"
                :content="$t('Suppliers.CheckSupplier')"
                placement="bottom"
                popper-class="h8-text py-3"
                type="white">
                <IconCount
                  :count="item.blockingCount"
                  icon="stopped-person"
                  :icon-color="item.blockingCount ? 'var(--color-error)' : 'var(--green_chosen)'"
                  :text-class="(item.blockingCount ? 'secondary__red' : 'secondary__green_approved') + '--text'"/>
              </sTooltip>
            </div>
          </KeyValue>
          <div class="text-left pt-4">
            <RatingDialog
              :organization-id="item.supplierOrganizationId"
              :organization-title="item.organizationTitle"
              @changeRatings="getRating"/>
          </div>
        </div>
      </div>
    </div>
    <v-expand-transition>
      <div
        v-show="opened"
        class="flex">
        <div
          id="chart-donut-bids"
          class="chart-donut">
          <SpilkaChartsMF
            v-if="growthBook.mf_apex"
            v-bind="donutMfPayload('bids')"
            height="140"
            width="255"/>
          <ApexChartDonut
            v-else
            :chart-options="chartOptions"
            height="140"
            :series="series('bids')"
            width="255"/>
        </div>
        <div
          v-if="item.wins && item.wins.count "
          id="chart-donut-wins"
          class="chart-donut">
          <SpilkaChartsMF
            v-if="growthBook.mf_apex"
            v-bind="donutMfPayload('wins')"
            height="140"
            width="255"/>
          <ApexChartDonut
            v-else
            :chart-options="chartOptions"
            height="140"
            :series="series('wins')"
            width="255"/>
        </div>
      </div>
    </v-expand-transition>
  </div>
</template>

<script>
import KeyValue from 'components/common/KeyValue.vue'
import IconCount from 'components/common/elements/IconCount.vue'
import ApexChartDonut from 'components/charts/ApexChartDonut.vue'
import SpilkaChartsMF from 'components/common/SpilkaChartsMF.vue'
import StatusChip from 'components/common/elements/StatusChip.vue'
import SupplierOnSite from 'components/supplies/SupplierOnSite.vue'
import RatingDialog from 'components/common/dialogs/RatingDialog.vue'

import 'moment-precise-range-plugin'
import { modalMixin, supplierMixin } from 'mixins'

export default {
  name: 'supplierCard',
  mixins: [ modalMixin, supplierMixin],
  props: {
    item: Object,
    last: Boolean,
    opened: {
      type: Boolean
    }
  },
  computed: {
    chartOptions() {
      return {
        chart: {
          width: 120,
          height: 120,
          offsetX: -25,
          type: 'donut',
        },
        states: {
          active: {
            allowMultipleDataPointsSelection: false,
            filter: {
              type: 'none',
            }
          },
        },
        tooltip: {
          enabled: false,
        },
        labels: [ this.$t('Suppliers.InMine'),this.$t('Suppliers.InOther')],
        colors: [ 'var(--color-info)', 'var(--color-success)'],
        stroke: {
          show: true,
          colors: ['var(--v-secondary__green_chosen-base)'],
          width: 1
        },
        legend: {
          offsetX: 10,
          formatter: (seriesName, opts) => {
            return `<div class="h9-label text-left "> ${opts.w.config.labels[ opts.seriesIndex]} </div>
                    <div class="h7-text text-left"> ${opts.w.config.series[ opts.seriesIndex]} </div>`
          },
        },
        plotOptions: {
          pie: {
            startAngle: 10,
            expandOnClick: false,
            dataLabels: false,
            donut: {
              size: '15%',
              labels: {
                show: false,
              }
            },
          }
        },
      }
    },
  },
  methods: {
    async getRating() {
      await this.$store.dispatch('getSupplierRatings', {supplierOrgId: Number(this.item.supplierOrganizationId)})
        .then(res => {
          if (res) {
            // eslint-disable-next-line vue/no-mutating-props
            this.item.rating = res.totalScore
          }
        })
    },
    openAll() {
      const newState = !this.opened
      this.$emit('update:opened', newState)
      this.$emit(newState ? 'open' : 'close', this.item.supplierOrganizationId)
    },
    series(type) {
      return [this.item[type].countInOwnTender, this.item[type].count - this.item[[type]].countInOwnTender]
    },
    donutMfPayload(type) {
      return {
        kind: 'donut',
        variant: 'plain',
        labels: [ this.$t('Suppliers.InMine'), this.$t('Suppliers.InOther') ],
        series: this.series(type),
        colors: [ 'info', 'success' ],
        strokeColor: 'green-chosen',
        tooltipEnabled: false,
        size: '15%',
      }
    },
    bidsInAmountOf(bids, amount) {
      let count = this.formSum(bids) || '–',
        summ = amount ? ` ${this.$t('Suppliers.InAmountOf')} ${this.formSum(amount)} ${this.$t('Tenders.hrn')}` : ''
      return count + summ
    }
  },
  components: {
    KeyValue,
    IconCount,
    StatusChip,
    RatingDialog,
    ApexChartDonut,
    SpilkaChartsMF,
    SupplierOnSite
  }
}
</script>
<style lang="scss" scoped>
.v-list {
  background-color: inherit;
}
.v-list-item {
  min-height: 30px ;
}
</style>
<style lang="scss">
.supplier-card {
  .chart-donut {
    width: 240px;
    .apexcharts-legend-series {
      display: flex;
    }
  }

  /* .in-development-block {
    &, div, .black-filled-btn {
      width: 100%;
    }
  } */
  .v-list-item {
    .v-list-item__title, .v-list-item__subtitle {
      white-space: normal !important;
    }
  }
}
</style>
